import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, Save, Loader2, Replace, BookOpen, LayoutTemplate, Check } from 'lucide-react';
import { api, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import CoachShell from '@/components/CoachShell';
import { LoadingScreen } from '@/components/State';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import LibraryPickerModal from '@/components/LibraryPickerModal';

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const newMeal = () => ({ id: uid(), name: '', time: '', calories: '', protein: '', carbs: '', fat: '', items: '', alternatives: '', prep: '' });
const altStr = (a) => (Array.isArray(a) ? a.map((x) => x.name || x.items).filter(Boolean).join(', ') : (a || ''));
const nsum = (meals, k) => meals.reduce((s, m) => s + (parseFloat(String(m[k]).replace(',', '.')) || 0), 0);
const recalcDaily = (meals) => ({
  daily_calories: Math.round(nsum(meals, 'calories')),
  daily_protein: Math.round(nsum(meals, 'protein')),
  daily_carbs: Math.round(nsum(meals, 'carbs')),
  daily_fat: Math.round(nsum(meals, 'fat')),
});
const rebuildShopping = (meals) => {
  const out = [];
  meals.forEach((m) => String(m.items || '').split(/[,\n]/).forEach((x) => {
    const v = x.trim();
    if (v && !out.includes(v)) out.push(v);
  }));
  return out.join('\n');
};

export default function EditNutrition() {
  const { id } = useParams();
  const { t, tplName, catLabel } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState({ daily_calories: '', daily_protein: '', daily_carbs: '', daily_fat: '', notes: '', shopping_list: '', supplements: '', start_date: '', meals: [] });
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [applyingId, setApplyingId] = useState(null);

  const loadPlan = useCallback(async () => {
    const res = await api.get(`/nutrition-plans/${id}?t=${Date.now()}`);
    const p = res.data;
    if (p) setPlan({
      daily_calories: p.daily_calories ?? '', daily_protein: p.daily_protein ?? '', daily_carbs: p.daily_carbs ?? '', daily_fat: p.daily_fat ?? '',
      notes: p.notes || '', shopping_list: p.shopping_list || '', supplements: p.supplements || '', start_date: (p.start_date || '').slice(0, 10),
      meals: (p.meals || []).map((m) => ({ ...m, alternatives: Array.isArray(m.alternatives) ? m.alternatives.map((a) => a.name || a.items).join(', ') : (m.alternatives || '') })),
    });
    else setPlan((s) => ({ ...s, meals: [newMeal()] }));
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        await loadPlan();
        try {
          const tp = await api.get('/nutrition-templates');
          setTemplates(tp.data?.templates || tp.data || []);
        } catch (e) { /* templates optional */ }
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, [loadPlan]);

  const tplMealCount = (tpl) => {
    const dd = tpl.weeks?.[0]?.days || tpl.days || [];
    return (dd[0]?.meals || []).length;
  };

  const applyTemplate = async (tpl) => {
    setApplyingId(tpl.id);
    try {
      await api.post(`/nutrition-templates/${tpl.id}/apply/${id}`);
      toast.success(`${tplName(tpl)} ✓`);
      setTplOpen(false);
      await loadPlan();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setApplyingId(null); }
  };

  const setField = (k, v) => setPlan((p) => ({ ...p, [k]: v }));
  const updMeal = (i, patch) => setPlan((p) => {
    const meals = p.meals.map((m, j) => (j === i ? { ...m, ...patch } : m));
    return { ...p, meals, ...recalcDaily(meals) };
  });

  const [mealPicker, setMealPicker] = useState(null); // { index | null }

  const onPickMeal = (lib) => {
    const idx = mealPicker?.index;
    setPlan((p) => {
      let meals;
      const libData = {
        items: lib.items || '', alternatives: altStr(lib.alternatives), prep: lib.prep || '',
        category: lib.category || '', library_id: lib.id,
        calories: lib.calories ?? '', protein: lib.protein ?? '', carbs: lib.carbs ?? '', fat: lib.fat ?? '',
      };
      if (idx === null || idx === undefined) {
        // Add from library — new meal (dish name + default time)
        meals = [...p.meals, { id: uid(), name: lib.name, time: lib.default_time || '', ...libData }];
      } else {
        // Replace — keep id, slot time & order; take name & nutritional content from library
        meals = p.meals.map((m, j) => (j === idx ? { ...m, name: lib.name, ...libData } : m));
      }
      return { ...p, meals, ...recalcDaily(meals), shopping_list: rebuildShopping(meals) };
    });
    setMealPicker(null);
  };

  const removeMeal = (i) => setPlan((p) => {
    const meals = p.meals.filter((_, j) => j !== i);
    return { ...p, meals, ...recalcDaily(meals), shopping_list: rebuildShopping(meals) };
  });
  const addBlankMeal = () => setPlan((p) => {
    const meals = [...p.meals, newMeal()];
    return { ...p, meals, ...recalcDaily(meals) };
  });

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => (v === '' || v === null ? null : parseFloat(String(v).replace(',', '.')));
      await api.post('/nutrition-plans', {
        client_id: id,
        daily_calories: num(plan.daily_calories), daily_protein: num(plan.daily_protein),
        daily_carbs: num(plan.daily_carbs), daily_fat: num(plan.daily_fat),
        notes: plan.notes, shopping_list: plan.shopping_list, supplements: plan.supplements,
        start_date: plan.start_date || null,
        meals: plan.meals.map((m) => ({
          id: m.id, name: m.name, time: m.time, items: m.items, alternatives: m.alternatives,
          prep: m.prep || '', category: m.category || '', library_id: m.library_id || null,
          calories: num(m.calories), protein: num(m.protein), carbs: num(m.carbs), fat: num(m.fat),
        })),
      });
      toast.success(t('edit.posted'));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <CoachShell back={`/coach/client/${id}`}><LoadingScreen /></CoachShell>;

  return (
    <CoachShell back={`/coach/client/${id}`} title={t('edit.nutrition')}>
      <button
        onClick={() => setTplOpen(true)}
        data-testid="open-nutrition-template-picker"
        className="mb-5 flex w-full items-center justify-center gap-2 rounded-xl border border-asgym-accent/40 bg-asgym-accent/10 py-4 text-sm font-extrabold tracking-wide text-asgym-accent transition-colors hover:bg-asgym-accent/15"
      >
        <LayoutTemplate size={17} /> {t('edit.applyTemplate')}
      </button>

      <div className="as-card mb-5 p-4">
        <label className="as-label mb-1.5 block">{t('edit.startDate')}</label>
        <input
          type="date"
          data-testid="nutrition-start-date"
          value={plan.start_date || ''}
          onChange={(e) => setField('start_date', e.target.value)}
          className="h-11 w-full rounded-lg border border-white/[0.06] bg-black/20 px-3 text-sm font-bold text-white [color-scheme:dark]"
        />
        <p className="mt-1.5 text-[11px] leading-snug text-asgym-muted">{t('edit.startDateHint')}</p>
      </div>

      <div className="as-card mb-5 grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
        {[['daily_calories', 'edit.kcal'], ['daily_protein', 'edit.protein'], ['daily_carbs', 'edit.carbs'], ['daily_fat', 'edit.fat']].map(([k, tk]) => (
          <div key={k}>
            <label className="as-label mb-1.5 block">{t(tk)}</label>
            <div className="flex h-11 items-center rounded-lg border border-white/[0.06] bg-black/20 px-3 text-sm font-bold text-white" data-testid={`macro-${k}`}>{plan[k] || 0}</div>
          </div>
        ))}
      </div>

      <p className="as-section mb-3">{t('nutrition.meals')}</p>
      <div className="space-y-3">
        {plan.meals.map((m, i) => (
          <div key={m.id} className="as-card p-4" data-testid={`edit-meal-${i}`}>
            <div className="mb-2 flex gap-2">
              <input className="as-input-sm flex-1 font-bold" value={m.name} onChange={(e) => updMeal(i, { name: e.target.value })} placeholder={t('edit.mealName')} data-testid={`meal-name-${i}`} />
              <input className="as-input-sm w-20" value={m.time || ''} onChange={(e) => updMeal(i, { time: e.target.value })} placeholder={t('edit.mealTime')} />
              <button onClick={() => setMealPicker({ index: i })} data-testid={`replace-meal-${i}`} title={t('edit.replaceMeal')} className="flex h-11 shrink-0 items-center gap-1 rounded-lg border border-white/[0.08] bg-white/[0.03] px-2.5 text-[11px] font-bold text-asgym-sub hover:text-asgym-accent"><Replace size={13} /> {t('edit.replaceShort')}</button>
              <button onClick={() => removeMeal(i)} data-testid={`delete-meal-${i}`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-asgym-error/30 bg-asgym-error/10 text-asgym-error"><Trash2 size={15} /></button>
            </div>
            <div className="mb-2 grid grid-cols-4 gap-2">
              {[['calories', 'K'], ['protein', 'P'], ['carbs', 'C'], ['fat', 'F']].map(([k, lbl]) => (
                <div key={k} className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-black/30 px-2.5">
                  <span className="text-[11px] font-bold text-asgym-muted">{lbl}</span>
                  <input className="h-10 w-full bg-transparent text-sm text-white outline-none" inputMode="decimal" value={m[k] ?? ''} onChange={(e) => updMeal(i, { [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <textarea className="as-input mb-2 h-16 resize-none py-2.5 text-sm" value={m.items || ''} onChange={(e) => updMeal(i, { items: e.target.value })} placeholder={t('edit.mealItems')} />
            <textarea className="as-input mb-2 h-16 resize-none py-2.5 text-sm" value={m.prep || ''} onChange={(e) => updMeal(i, { prep: e.target.value })} placeholder={t('edit.mealPrep')} data-testid={`meal-prep-${i}`} />
            <input className="as-input-sm" value={m.alternatives || ''} onChange={(e) => updMeal(i, { alternatives: e.target.value })} placeholder={t('edit.mealAlt')} />
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={addBlankMeal} className="as-btn-ghost h-12 flex-1" data-testid="add-meal-btn"><Plus size={16} /> {t('edit.addMeal')}</button>
        <button onClick={() => setMealPicker({ index: null })} className="as-btn-ghost h-12 flex-1" data-testid="add-meal-lib-btn"><BookOpen size={16} /> {t('edit.addMealFromLibrary')}</button>
      </div>

      <div className="mt-6 space-y-4">
        {[['notes', 'edit.notes2'], ['shopping_list', 'edit.shopping'], ['supplements', 'edit.supplements']].map(([k, tk]) => (
          <div key={k}>
            <label className="as-label mb-2 block">{t(tk)}</label>
            <textarea className="as-input h-24 resize-none py-3 text-sm" value={plan[k]} onChange={(e) => setField(k, e.target.value)} />
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} className="as-btn-primary sticky bottom-4 mt-6 w-full" data-testid="save-nutrition-btn">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={16} /> {t('edit.savePlan')}</>}
      </button>

      <LibraryPickerModal
        open={!!mealPicker}
        onOpenChange={(o) => !o && setMealPicker(null)}
        type="meal"
        onSelect={onPickMeal}
      />

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-asgym-surface text-asgym-text sm:max-w-lg" data-testid="nutrition-template-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-white">
              {t('edit.chooseNutritionTemplate')}
            </DialogTitle>
            <DialogDescription className="text-asgym-sub">{t('edit.applyTemplateHint')}</DialogDescription>
          </DialogHeader>
          {templates.length === 0 ? (
            <p className="py-8 text-center text-sm text-asgym-sub">{t('edit.noTemplates')}</p>
          ) : (
            <div className="space-y-2.5 pt-2">
              {templates.map((tpl) => (
                <div key={tpl.id} data-testid={`nutrition-template-option-${tpl.id}`} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-black/20 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-bold text-white">{tplName(tpl)}</p>
                    <p className="text-[11px] text-asgym-muted">
                      {tpl.category ? `${catLabel(tpl.category)} · ` : ''}{tplMealCount(tpl)} {t('edit.mealsCount')}
                    </p>
                  </div>
                  <button
                    onClick={() => applyTemplate(tpl)}
                    disabled={!!applyingId}
                    data-testid={`apply-nutrition-template-${tpl.id}`}
                    className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg bg-asgym-accent px-4 text-xs font-extrabold tracking-wide text-black transition-transform active:scale-95 disabled:opacity-50"
                  >
                    {applyingId === tpl.id ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />}
                    {t('edit.apply')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </CoachShell>
  );
}
